import { useCallback, useEffect, useRef, useState } from "react";
import { Dialog, DialogBackdrop, DialogPanel } from "@headlessui/react";
import {
  ArrowPathIcon,
  ChevronLeftIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import {
  ArrowUpIcon,
  CheckIcon,
  SparklesIcon,
} from "@heroicons/react/24/solid";
import TaggedUsers from "@components/TaggedUsers";
import { Markdown } from "@areas/chatbot/utils";
import {
  PayerTypeLabel,
  WatchFlagChips,
} from "@areas/trade-accounts/components/FactCells";
import AiSparkleIcon from "./AiSparkleIcon";
import AiSpinner from "./AiSpinner";
import SuggestionIcon from "./SuggestionIcon";
import useTypewriter from "./useTypewriter";

// Fake account used by the read stage so it mirrors the real customer context panel — name,
// credit code and watch flags, with illustrative values.
const DEMO_ACCOUNT = {
  name: "Meridian Building Supplies",
  creditCode: "STOP",
  watchFlags: ["OverdueJumped", "New30DaysPastDue", "OrderPause"],
};

// How long each stage stays up before the carousel auto-advances. Long enough for a chat
// stage to type its message, get a suggestion and accept it, then rest for a beat.
const AUTO_MS = 8125;
const TICK_MS = 40;
// How far a swipe/drag must travel to flip to the next stage.
const SWIPE_THRESHOLD = 48;

// The scripted beats of a chat stage (ms from when it becomes active).
const CHAT_BEATS = {
  sent: 2063,
  thinking: 2563,
  suggested: 3813,
  accepting: 5313,
  done: 6188,
};
const CHAT_ORDER = ["typing", "sent", "thinking", "suggested", "accepting", "done"];
const COMPOSER_PLACEHOLDER =
  "You can add a note, send a request or type @ to notify a colleague";

const SLIDES = [
  {
    eyebrow: "Instant context",
    title: "Know any account in seconds",
    body: "Pick a customer and Ontik reads their whole story instantly — risk, exposure and what to do next.",
    demo: {
      kind: "read",
      lines: [
        "⚠️ **Risk sentiment** — Account sits on hard stop with a volatile credit code",
        "📉 **Credit status unstable** — ERP code flipped 3× in a week",
        "📨 **Chases escalating, nothing paid** — 4 requests, £10.5k → £19.2k",
        "🛑 **Order pause, arrears rising** — new 30-day breach, oldest ~2 months",
      ],
    },
  },
  {
    eyebrow: "Instant answers",
    title: "Ask anything about an account",
    body: "Ask a question in plain English and get the numbers straight back — no reports to run.",
    demo: {
      kind: "chat",
      prompt: "What are this customer's open invoices?",
      reply: [
        "Here are the open invoices for this customer:",
        "",
        "| Invoice | Due | Amount | Status |",
        "| --- | --- | --- | --- |",
        "| INV-10432 | 12 Aug | £1,240 | Overdue |",
        "| INV-10461 | 28 Aug | £1,880 | Overdue |",
        "| INV-10499 | 14 Sep | £1,000 | Due soon |",
        "| **Total** | | **£4,120** | |",
      ].join("\n"),
    },
  },
  {
    eyebrow: "No more digging",
    title: "Send the right documents",
    body: "Invoices, statements, credit notes and proof of delivery — sent straight over, without hunting through folders.",
    demo: {
      kind: "chat",
      prompt: "Send them a copy of invoice 10432",
      card: {
        type: "SendEmailRequested",
        label: "Send documents",
        body: "Send invoice 10432",
        sendingTo: "andrew@acme.co",
        button: "Send",
      },
    },
  },
  {
    eyebrow: "Get paid faster",
    title: "Chase payments in plain English",
    body: "Just ask. Ontik drafts and sends payment requests over email and SMS in a single click.",
    demo: {
      kind: "chat",
      prompt: "Chase the overdue invoices",
      card: {
        type: "PaymentRequestRequested",
        label: "Payment request",
        body: "£4,120 across 3 overdue invoices",
        sendingTo: "andrew@acme.co · 07404 314 999",
        button: "Send",
      },
    },
  },
  {
    eyebrow: "Spread the load",
    title: "Set up payment plans",
    body: "Break a balance into instalments and Ontik schedules the whole run for you.",
    demo: {
      kind: "chat",
      prompt: "Split the balance over 3 months",
      card: {
        type: "PaymentPlan",
        label: "Payment plan",
        body: "3 instalments of £1,373 · monthly from 30 Sep",
        button: "Create",
      },
    },
  },
  {
    eyebrow: "Never lose a commitment",
    title: "Capture promises to pay",
    body: "Log what a customer commits to and Ontik keeps track — chasing automatically if the date slips.",
    demo: {
      kind: "chat",
      prompt: "Andrew said he'll pay by Friday",
      card: {
        type: "PromiseToPay",
        label: "Promise to pay",
        body: "£4,120 · Fri, 12 Sep 2026 at 8am",
        button: "Create",
      },
    },
  },
  {
    eyebrow: "Nothing gets lost",
    title: "Log every interaction",
    body: "Jot a note the way you'd say it out loud. Ontik tidies it up and files it against the account.",
    demo: {
      kind: "chat",
      prompt: "Spoke to Andrew — he's disputing the delivery charge",
      card: {
        type: "CustomerInteractionNoted",
        label: "Add note",
        body: "Customer is disputing the delivery charge on invoice 10432.",
        note: true,
        button: "Add",
      },
    },
  },
  {
    eyebrow: "Bring in the team",
    title: "Loop in your colleagues",
    body: "Type @ to tag someone — they're notified instantly and the note lands on the account.",
    demo: {
      kind: "chat",
      prompt: "@Sarah Hughes can you take a look at this dispute?",
      mention: "@Sarah Hughes",
      card: {
        type: "CustomerInteractionNoted",
        label: "Add note",
        body: "Can you take a look at this dispute?",
        note: true,
        taggedUsers: [{ id: "1", name: "Sarah Hughes" }],
        button: "Add",
      },
    },
  },
  {
    eyebrow: "One prompt, many actions",
    title: "Do several things at once",
    body: "Ask for everything in one line — Ontik works out each action and lines them up ready to go.",
    demo: {
      kind: "chat",
      prompt: "Chase the balance and log that I spoke to Andrew",
      cards: [
        {
          type: "PaymentRequestRequested",
          label: "Payment request",
          body: "£4,120 across 3 overdue invoices",
          sendingTo: "andrew@acme.co · 07404 314 999",
          button: "Send",
        },
        {
          type: "CustomerInteractionNoted",
          label: "Add note",
          body: "Spoke to Andrew about the overdue balance.",
          note: true,
          button: "Add",
        },
      ],
    },
  },
  {
    eyebrow: "Total control",
    title: "Change your mind anytime",
    body: "Scheduled something by mistake? Tell Ontik to cancel it and it's undone — no menus, no fuss.",
    demo: {
      kind: "chat",
      prompt: "Actually, cancel that payment request",
      card: {
        type: "CancellationRequested",
        label: "Cancel payment request",
        body: "£4,120 · scheduled for tomorrow",
        button: "Confirm",
        danger: true,
      },
    },
  },
];

// The user's typed message — same bubble as the real chat thread, with any @-mention
// picked out in blue like the composer does.
function PromptBubble({ text, mention }) {
  const parts = mention ? text.split(mention) : [text];
  return (
    <div className="flex flex-col items-end gap-1">
      <div className="max-w-[85%] rounded-2xl bg-white px-4 py-2.5 text-sm text-zinc-900 shadow-sm">
        {mention ? (
          <>
            {parts[0]}
            <span className="font-medium text-blue-600">{mention}</span>
            {parts.slice(1).join(mention)}
          </>
        ) : (
          text
        )}
      </div>
    </div>
  );
}

// A faithful, static rendering of a real DraftSuggestionCard — same tile, spacing, label +
// body treatment and affordance. `state` mirrors the real accept UX: idle shows the text
// button, "accepting" swaps in the spinner, "done" flips to the green tick.
function ResultCard({ card, state = "idle" }) {
  return (
    <div className="relative flex flex-col gap-4 rounded-2xl border border-zinc-200 bg-white pt-6 px-4 pb-4 shadow-sm">
      <span
        aria-hidden="true"
        className="pointer-events-none absolute right-4 top-3 text-zinc-400"
      >
        <XMarkIcon className="size-3.5" />
      </span>

      <div className="flex items-center gap-3">
        {/* Hollow tile: gradient outline with a gradient glyph on a white fill. */}
        <span className="shrink-0 rounded-xl bg-[linear-gradient(135deg,#ec4899,#a855f7,#3b82f6,#22d3ee,#fb923c)] p-px">
          <span className="flex size-9 items-center justify-center rounded-[11px] bg-white">
            <SuggestionIcon type={card.type} className="size-5" />
          </span>
        </span>

        <div className="min-w-0 flex-1">
          <div className="text-xs text-zinc-500">{card.label}</div>
          <div
            className={
              card.note
                ? "mt-0.5 text-sm italic text-zinc-900"
                : "mt-0.5 text-sm text-zinc-600"
            }
          >
            {card.note ? `“${card.body}”` : card.body}
          </div>
          {card.sendingTo && (
            <div className="mt-1 text-xs text-zinc-400">
              Sending to: {card.sendingTo}
            </div>
          )}
          {card.taggedUsers && <TaggedUsers users={card.taggedUsers} />}
        </div>

        {state === "done" ? (
          <span className="animate-tickIn flex size-7 min-w-[2.5rem] items-center justify-center">
            <span className="flex size-7 items-center justify-center rounded-full bg-green-600">
              <CheckIcon
                aria-hidden="true"
                strokeWidth={2.5}
                className="size-4 text-white"
              />
            </span>
          </span>
        ) : (
          <span
            className={`flex min-w-[2.5rem] items-center justify-center rounded text-sm font-semibold ${
              card.danger ? "text-red-600" : "text-zinc-900"
            }`}
          >
            {state === "accepting" ? <AiSpinner size={18} /> : card.button}
          </span>
        )}
      </div>
    </div>
  );
}

// Renders demo copy with **bold** spans picked out (the only markdown used in the read).
function ReadLine({ text }) {
  return text.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    part.startsWith("**") && part.endsWith("**") ? (
      <span key={i} className="font-semibold text-zinc-900">
        {part.slice(2, -2)}
      </span>
    ) : (
      <span key={i}>{part}</span>
    ),
  );
}

// The "Ontik read" — same white card, label and refresh affordance as the real summary.
// When `animate`, the card blurs in and its heading, summary and lines reveal in sequence.
function ReadCard({ title, summary, lines, animate }) {
  // Reveal delay for the nth revealed element (heading = 0, summary = 1, lines follow).
  const revealStyle = (i) =>
    animate
      ? { animationDelay: `${313 + i * 325}ms`, animationDuration: "1250ms" }
      : undefined;
  const anim = animate ? "animate-readIn" : "";

  return (
    <div
      className={`w-full rounded-2xl border border-zinc-200 bg-white p-4 shadow-sm ${
        animate ? "animate-fadeIn" : ""
      }`}
      style={animate ? { animationDuration: "875ms" } : undefined}
    >
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-zinc-500">
          <AiSparkleIcon className="size-4" />
          Ontik read
        </span>
        <ArrowPathIcon aria-hidden="true" className="size-4 text-zinc-400" />
      </div>

      {title && (
        <p className={`mt-3 text-sm font-semibold text-zinc-900 ${anim}`} style={revealStyle(0)}>
          {title}
        </p>
      )}
      {summary && (
        <p className={`mt-1 text-sm leading-snug text-zinc-600 ${anim}`} style={revealStyle(1)}>
          {summary}
        </p>
      )}

      <div className="mt-2.5 space-y-1.5">
        {lines.map((line, li) => (
          <p
            key={line}
            className={`text-sm leading-snug text-zinc-700 ${anim}`}
            style={revealStyle(li + 2)}
          >
            <ReadLine text={line} />
          </p>
        ))}
      </div>
    </div>
  );
}

// The composer at the bottom of a chat stage — a simplified copy of the real input row.
// While typing, the message reveals inside it with a caret and the send button lights up.
function DemoComposer({ text, active }) {
  return (
    <div
      className={`rounded-2xl border bg-white p-3 transition-colors ${
        active ? "border-primary" : "border-zinc-300"
      }`}
    >
      <div className="min-h-[20px] text-sm">
        {text ? (
          <span className="text-zinc-900">
            {text}
            <span className="ml-px inline-block h-4 w-px translate-y-0.5 bg-zinc-500" />
          </span>
        ) : (
          <span className="text-zinc-400">{COMPOSER_PLACEHOLDER}</span>
        )}
      </div>
      <div className="mt-3 flex items-center justify-end">
        <span
          className={`flex size-9 items-center justify-center rounded-xl text-white transition-colors ${
            text ? "bg-primary-500" : "bg-primary-300"
          }`}
        >
          <ArrowUpIcon aria-hidden="true" className="size-5" />
        </span>
      </div>
    </div>
  );
}

// A self-playing demo of the chat: the message types into the composer, sends as a bubble,
// Ontik "thinks", the suggestion appears, then it's accepted — flipping to the green tick,
// the same UX as a real acceptance. Restarts whenever the stage is (re)activated.
function ChatDemo({ demo, animate }) {
  const [phase, setPhase] = useState(animate ? "typing" : "done");
  const typed = useTypewriter(animate && phase === "typing" ? demo.prompt : "", {
    durationMs: 1688,
  });

  useEffect(() => {
    if (!animate) {
      setPhase("done");
      return undefined;
    }
    setPhase("typing");
    const timers = [
      setTimeout(() => setPhase("sent"), CHAT_BEATS.sent),
      setTimeout(() => setPhase("thinking"), CHAT_BEATS.thinking),
      setTimeout(() => setPhase("suggested"), CHAT_BEATS.suggested),
      setTimeout(() => setPhase("accepting"), CHAT_BEATS.accepting),
      setTimeout(() => setPhase("done"), CHAT_BEATS.done),
    ];
    return () => timers.forEach(clearTimeout);
  }, [animate]);

  const reached = (p) => CHAT_ORDER.indexOf(phase) >= CHAT_ORDER.indexOf(p);
  const cardState =
    phase === "done" ? "done" : phase === "accepting" ? "accepting" : "idle";

  return (
    <div className="flex min-h-[320px] flex-col gap-3 rounded-2xl bg-zinc-50 p-4">
      <div className="flex flex-1 flex-col justify-end gap-3">
        {reached("sent") && (
          <div className="animate-suggestionIn" style={{ animationDuration: "625ms" }}>
            <PromptBubble text={demo.prompt} mention={demo.mention} />
          </div>
        )}
        {phase === "thinking" && (
          <div
            className="animate-fadeIn flex items-center gap-2 text-sm text-zinc-400"
            aria-live="polite"
          >
            <AiSpinner size={16} />
            <span>Ontik is thinking…</span>
          </div>
        )}
        {reached("suggested") &&
          (demo.reply ? (
            // Rendered exactly like a real assistant reply — the LLM's markdown (a GFM
            // table here) ingested through the shared chat Markdown renderer.
            <div className="animate-suggestionIn" style={{ animationDuration: "625ms" }}>
              <div className="w-full text-sm text-zinc-800">
                <Markdown>{demo.reply}</Markdown>
              </div>
            </div>
          ) : (
            // One prompt can yield several suggestions — each card lands in turn.
            (demo.cards ?? [demo.card]).map((card, ci) => (
              <div
                key={ci}
                className="animate-suggestionIn"
                style={{ animationDuration: "625ms", animationDelay: `${ci * 275}ms` }}
              >
                <ResultCard card={card} state={cardState} />
              </div>
            ))
          ))}
      </div>
      <DemoComposer text={phase === "typing" ? typed : ""} active={phase === "typing"} />
    </div>
  );
}

// The product demo for a stage — the scripted chat, or the "Ontik read" for the first stage.
// `token` bumps on each activation so the sequence restarts.
// The read stage mirrors the real customer context panel: name + credit code, the payer-type
// and watch-flag chips, the "Ontik read", and the aged-debt balance breakdown beneath it.
function ReadPanel({ demo, animate }) {
  return (
    <div
      className={animate ? "animate-fadeIn" : ""}
      style={animate ? { animationDuration: "563ms" } : undefined}
    >
      <div className="flex items-center justify-between gap-3">
        <h4 className="min-w-0 truncate text-base font-semibold text-zinc-900">
          {DEMO_ACCOUNT.name}
        </h4>
        <span className="inline-flex h-6 shrink-0 items-center rounded-md border border-zinc-200 bg-white px-2 text-xs font-medium text-zinc-700">
          {DEMO_ACCOUNT.creditCode}
        </span>
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-2">
        <PayerTypeLabel timingPersona="late" lateBand="verylate" />
        <WatchFlagChips flags={DEMO_ACCOUNT.watchFlags} />
      </div>

      <div className="mt-3">
        <ReadCard lines={demo.lines} animate={animate} />
      </div>
    </div>
  );
}

function Demo({ demo, animate, token }) {
  if (demo.kind === "read") {
    return (
      <div className="flex min-h-[320px] flex-col justify-center gap-4 rounded-2xl bg-zinc-50 p-4">
        <ReadPanel key={token} demo={demo} animate={animate} />
      </div>
    );
  }
  return <ChatDemo key={token} demo={demo} animate={animate} />;
}

// Spotlight shown on the first stage — a glowing replica of the floating button, in the exact
// spot the real one lives, so people learn where to reopen Ontik Intelligence afterwards. The
// real button is hidden behind the tour's backdrop, so this stands in for it.
function FabSpotlight() {
  return (
    <div className="pointer-events-none fixed bottom-6 right-6 z-20">
      {/* Callout pointing at the button */}
      <div className="animate-fadeIn absolute bottom-full right-0 mb-5 w-60 rounded-2xl bg-white p-4 shadow-2xl">
        <p className="text-sm font-semibold text-zinc-900">
          Find it here anytime
        </p>
        <p className="mt-1 text-sm leading-snug text-zinc-500">
          Reopen Ontik Intelligence from this button — or just press space.
        </p>
        <span className="absolute -bottom-1.5 right-8 size-3 rotate-45 rounded-[3px] bg-white" />
      </div>

      {/* Pulsing halo */}
      <span className="absolute -inset-1 animate-ping rounded-full bg-white/40" />

      {/* Replica of the real floating pill */}
      <div className="relative flex items-center gap-2.5 rounded-full bg-gradient-to-br from-violet-600 via-indigo-600 to-blue-600 py-3 pl-4 pr-5 text-white shadow-lg ring-4 ring-white/60">
        <SparklesIcon aria-hidden="true" className="size-5" />
        <span className="text-sm font-semibold">Ontik Intelligence</span>
      </div>
    </div>
  );
}

function Slide({ slide, shouldPlay, playId }) {
  // Remount the demo (via `token`) each time this slide is (re)activated so its parts replay.
  const [token, setToken] = useState(0);
  useEffect(() => {
    if (shouldPlay) setToken(playId);
  }, [shouldPlay, playId]);

  return (
    <div className="w-full shrink-0 px-6">
      <span className="bg-gradient-to-r from-pink-500 via-purple-500 to-orange-400 bg-clip-text text-sm font-semibold uppercase tracking-wide text-transparent">
        {slide.eyebrow}
      </span>
      <h3 className="mt-2 text-3xl font-semibold leading-tight text-zinc-900">
        {slide.title}
      </h3>
      <p className="mt-2 min-h-[56px] text-base leading-relaxed text-zinc-500">
        {slide.body}
      </p>
      <div className="mt-5">
        <Demo demo={slide.demo} animate={shouldPlay} token={token} />
      </div>
    </div>
  );
}

// First-run: the capabilities tour auto-opens the first time the flow is used, then stays
// behind the "Discover" button. Bump the version when the tour gains stages everyone
// should see — a new key means every browser counts as unseen and gets the tour once more.
// v2: Tasks, Notifications, Recipients and Side-panels stages.
export const TOUR_SEEN_KEY = "ontik-intelligence-tour-seen-v2";

// True the first time it's called for a key in this browser, then marks the key seen.
export function claimFirstView(key) {
  try {
    if (localStorage.getItem(key) === "1") return false;
    localStorage.setItem(key, "1");
    return true;
  } catch {
    return false; // storage unavailable (private mode etc.) — don't force anything open
  }
}

// Shared by the V1 and V2 Smart Note modals so seeing the tour in either counts for both.
export function useFirstRunTour(isOpen) {
  const [helpOpen, setHelpOpen] = useState(false);

  useEffect(() => {
    if (isOpen && claimFirstView(TOUR_SEEN_KEY)) setHelpOpen(true);
  }, [isOpen]);

  return [helpOpen, setHelpOpen];
}

export default function CapabilitiesHelp({ isOpen, onClose }) {
  const n = SLIDES.length;
  // `index` can reach `n` — a clone of the first stage tacked onto the end. Advancing past
  // the last real stage sweeps forward onto that clone, then we snap back to 0 with the
  // transition off, so the loop reads as continuous instead of rewinding through them all.
  const [index, setIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [dx, setDx] = useState(0);
  const [snapping, setSnapping] = useState(false);
  // Which slide should play its entrance, and a token that bumps on every navigation so a
  // slide replays even when revisited. Deliberately NOT touched by the loop-snap, so landing
  // back on stage 1 after a wrap stays settled (the clone already played it).
  const [animIndex, setAnimIndex] = useState(0);
  const [playId, setPlayId] = useState(0);
  // Once the user drives the carousel themselves (prev/next, a dot, or a swipe), stop the
  // auto-advance — they're in control now. Reset on each fresh open.
  const [interacted, setInteracted] = useState(false);

  const elapsedRef = useRef(0);
  const dragStartRef = useRef(0);
  const indexRef = useRef(0);
  const paused = dragging;
  const active = index % n;
  const isLast = index === n - 1;

  useEffect(() => {
    indexRef.current = index;
  }, [index]);

  const goTo = useCallback(
    (next) => {
      const target = Math.max(0, Math.min(next, n));
      elapsedRef.current = 0;
      setProgress(0);
      setIndex(target);
      setAnimIndex(target);
      setPlayId((p) => p + 1);
    },
    [n],
  );

  // User-driven navigation — stops the auto-advance for the rest of this session.
  const manualGoTo = useCallback(
    (next) => {
      setInteracted(true);
      goTo(next);
    },
    [goTo],
  );

  // Reset to the first stage (and re-enable auto-advance) each time the modal is opened.
  useEffect(() => {
    if (isOpen) {
      setInteracted(false);
      goTo(0);
    }
  }, [isOpen, goTo]);

  // Once the sweep onto the clone finishes, jump back to the real first stage with the
  // transition suppressed for one frame so nothing visibly moves.
  const handleTransitionEnd = (e) => {
    if (e.propertyName !== "transform" || index !== n) return;
    setSnapping(true);
    setIndex(0);
  };
  useEffect(() => {
    if (!snapping) return undefined;
    const id = requestAnimationFrame(() => setSnapping(false));
    return () => cancelAnimationFrame(id);
  }, [snapping]);

  // Auto-advance loop — a single timer drives both the progress fill and the stage change.
  // Stops for good once the user takes manual control, and pauses while mid-swipe.
  useEffect(() => {
    if (!isOpen || paused || interacted) return undefined;
    const id = setInterval(() => {
      elapsedRef.current += TICK_MS;
      if (elapsedRef.current >= AUTO_MS) {
        goTo(indexRef.current + 1);
      } else {
        setProgress(elapsedRef.current / AUTO_MS);
      }
    }, TICK_MS);
    return () => clearInterval(id);
  }, [isOpen, paused, interacted, n, goTo]);

  const onPointerDown = (e) => {
    dragStartRef.current = e.clientX;
    setDragging(true);
    setDx(0);
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e) => {
    if (dragging) setDx(e.clientX - dragStartRef.current);
  };
  const endDrag = () => {
    if (!dragging) return;
    if (dx <= -SWIPE_THRESHOLD) manualGoTo(index + 1);
    else if (dx >= SWIPE_THRESHOLD) manualGoTo(index - 1);
    setDragging(false);
    setDx(0);
  };

  const trackStyle = {
    transform: `translateX(calc(${-index * 100}% + ${dragging ? dx : 0}px))`,
    transition: dragging || snapping ? "none" : undefined,
  };

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-[60]">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-zinc-900/80 backdrop-blur-sm transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
      />

      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4 sm:p-6">
          <DialogPanel
            transition
            className="w-[760px] max-w-[calc(100vw-2rem)] overflow-hidden rounded-3xl bg-white text-left shadow-2xl transition-all focus:outline-none data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
          >
            {/* Header — just the close affordance; the stage's own title leads the content */}
            <div className="flex items-center justify-end px-6 pt-5">
              <button
                type="button"
                onClick={onClose}
                className="rounded-md p-1 text-zinc-400 hover:text-zinc-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600"
              >
                <span className="sr-only">Close</span>
                <XMarkIcon aria-hidden="true" className="size-5" />
              </button>
            </div>

            {/* Carousel viewport */}
            <div className="select-none pt-2">
              <div
                className="overflow-hidden"
                style={{ touchAction: "pan-y" }}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={endDrag}
                onPointerCancel={endDrag}
              >
                <div
                  className="flex transition-transform duration-[625ms] ease-in-out"
                  style={trackStyle}
                  onTransitionEnd={handleTransitionEnd}
                >
                  {[...SLIDES, SLIDES[0]].map((slide, i) => (
                    <Slide
                      key={i}
                      slide={slide}
                      shouldPlay={i === animIndex}
                      playId={playId}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Footer: Previous (left) · progress dots (centre) · Next (right) */}
            <div className="mt-7 grid grid-cols-3 items-center px-6 pb-6">
              <div className="justify-self-start">
                {index > 0 && (
                  <button
                    type="button"
                    onClick={() => manualGoTo(index - 1)}
                    className="inline-flex items-center gap-1 rounded-lg py-2 pr-3 text-sm font-semibold text-zinc-500 transition-colors hover:text-zinc-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600"
                  >
                    <ChevronLeftIcon aria-hidden="true" className="size-4" />
                    Previous
                  </button>
                )}
              </div>

              <div className="flex items-center justify-center gap-1.5">
                {SLIDES.map((slide, i) => (
                  <button
                    key={slide.title}
                    type="button"
                    onClick={() => manualGoTo(i)}
                    aria-label={`Go to ${slide.title}`}
                    className="group relative h-1.5 rounded-full"
                  >
                    <span
                      className={`block h-1.5 rounded-full transition-all ${
                        i === active
                          ? "w-6 bg-zinc-200"
                          : "w-1.5 bg-zinc-200 group-hover:bg-zinc-300"
                      }`}
                    />
                    {i === active && (
                      <span
                        className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-pink-500 to-orange-400"
                        style={{ width: `${Math.max(progress, 0.001) * 1.5}rem` }}
                      />
                    )}
                  </button>
                ))}
              </div>

              <div className="justify-self-end">
                <button
                  type="button"
                  onClick={() => (isLast ? onClose() : manualGoTo(index + 1))}
                  className="rounded-xl bg-primary-500 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-primary-600 active:bg-primary-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600"
                >
                  {isLast ? "Complete" : "Next"}
                </button>
              </div>
            </div>
          </DialogPanel>
        </div>
      </div>

      {active === 0 && <FabSpotlight />}
    </Dialog>
  );
}
